import Icon from '@/components/ui/AppIcon';

interface Activity {
  title: string;
  description: string;
}

interface Deliverable {
  name: string;
  icon: string;
}

interface PhaseCardProps {
  phaseNumber: number;
  title: string;
  subtitle: string;
  duration: string;
  activities: Activity[];
  deliverables: Deliverable[];
  keyOutcomes: string[];
  isLast?: boolean;
}

const PhaseCard = ({
  phaseNumber,
  title,
  subtitle,
  duration,
  activities,
  deliverables,
  keyOutcomes,
  isLast = false
}: PhaseCardProps) => {
  return (
    <div className="relative">
      <div className="bg-card border border-border rounded-lg p-5 sm:p-6 lg:p-8 institutional-shadow hover:shadow-xl transition-base">
        {/* Phase Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5 sm:mb-6">
          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
            <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-institutional text-institutional-foreground rounded-lg font-bold text-xl sm:text-2xl flex-shrink-0">
              {phaseNumber}
            </div>
            <div>
              <h3 className="text-lg sm:text-2xl font-semibold text-text-primary mb-1">{title}</h3>
              <p className="text-xs sm:text-sm text-text-secondary">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-text-secondary bg-surface px-3 sm:px-4 py-2 rounded-md self-start sm:self-auto flex-shrink-0">
            <Icon name="ClockIcon" size={14} variant="outline" />
            <span className="font-medium">{duration}</span>
          </div>
        </div>

        {/* Key Activities */}
        <div className="mb-5 sm:mb-6">
          <h4 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center">
            <Icon name="ListBulletIcon" size={18} variant="outline" className="mr-2 text-institutional" />
            Key Activities
          </h4>
          <div className="space-y-2 sm:space-y-3">
            {activities.map((activity, index) => (
              <div key={index} className="pl-3 sm:pl-4 border-l-2 border-institutional/30">
                <p className="font-medium text-text-primary mb-1 text-sm sm:text-base">{activity.title}</p>
                <p className="text-xs sm:text-sm text-text-secondary">{activity.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Deliverables */}
        <div className="mb-5 sm:mb-6">
          <h4 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center">
            <Icon name="DocumentCheckIcon" size={18} variant="outline" className="mr-2 text-institutional" />
            Deliverables
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {deliverables.map((deliverable, index) => (
              <div key={index} className="flex items-center space-x-2 sm:space-x-3 bg-surface p-2 sm:p-3 rounded-md">
                <Icon name={deliverable.icon as any} size={18} variant="outline" className="text-institutional flex-shrink-0" />
                <span className="text-xs sm:text-sm text-text-primary">{deliverable.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Key Outcomes */}
        <div>
          <h4 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center">
            <Icon name="CheckCircleIcon" size={18} variant="solid" className="mr-2 text-success" />
            Key Outcomes
          </h4>
          <ul className="space-y-1.5 sm:space-y-2">
            {keyOutcomes.map((outcome, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon name="CheckIcon" size={14} variant="outline" className="text-success mt-1 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-text-secondary">{outcome}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Connection Arrow */}
      {!isLast && (
        <div className="hidden lg:flex items-center justify-center my-6 sm:my-8">
          <Icon name="ArrowDownIcon" size={28} variant="outline" className="text-institutional animate-bounce" />
        </div>
      )}
    </div>
  );
};

export default PhaseCard;